import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { useAuth } from '../../context/AuthContext';
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Calendar, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Importer correctement les services Google
import { linkGoogleAccount, isGoogleAccountLinked } from '../../services/google/auth';
import { getCalendarList, saveCalendarPreferences } from '../../services/google/calendar';

interface CalendarItem {
  id: string;
  summary: string;
  primary?: boolean;
  backgroundColor?: string;
  selected?: boolean;
}

export const AdminSettings: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [calendars, setCalendars] = useState<CalendarItem[]>([]);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Vérifier si le compte Google est déjà lié
  useEffect(() => {
    const checkGoogleLink = async () => {
      if (currentUser) {
        setLoading(true);
        try {
          const linked = await isGoogleAccountLinked();
          setIsGoogleLinked(linked);
          
          if (linked) {
            await fetchCalendars();
          }
        } catch (error) {
          console.error("Erreur lors de la vérification de la liaison Google:", error);
          setLinkError(t('settings.errors.checkLinkFailed', 'Impossible de vérifier votre compte Google'));
        } finally {
          setLoading(false);
        }
      }
    };
    
    checkGoogleLink();
  }, [currentUser, t]);

  const fetchCalendars = async () => {
    try {
      setLoading(true);
      const calendarList = await getCalendarList();
      
      // Vérifier si calendarList est un tableau
      if (Array.isArray(calendarList)) {
        setCalendars(calendarList);
        // Par défaut, tous les calendriers sont sélectionnés
        setSelectedCalendars(calendarList.map((cal: CalendarItem) => cal.id));
      } else if (calendarList && typeof calendarList === 'object') {
        // Utiliser une assertion de type ou une vérification de propriété sécurisée
        const calendarData = calendarList as Record<string, any>;
        
        // Vérifier si la propriété items existe et est un tableau
        if (calendarData.items && Array.isArray(calendarData.items)) {
          const formattedCalendars = calendarData.items.map((item: any) => ({
            id: item.id || `calendar-${Math.random().toString(36).substr(2, 9)}`,
            summary: item.summary || 'Calendrier sans nom',
            primary: !!item.primary,
            backgroundColor: item.backgroundColor || '#4285F4',
            selected: true
          }));
          
          setCalendars(formattedCalendars);
          setSelectedCalendars(formattedCalendars.map((cal: CalendarItem) => cal.id));
        } else {
          console.error("Pas de calendriers trouvés dans la réponse:", calendarList);
          setCalendars([]);
          setSelectedCalendars([]);
        }
      } else {
        // Si ce n'est pas un tableau ni un objet avec items, définir un tableau vide
        console.error("Format de calendrier invalide:", calendarList);
        setCalendars([]);
        setSelectedCalendars([]);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des calendriers:", error);
      setLinkError(t('settings.errors.fetchCalendarsFailed', 'Impossible de récupérer vos calendriers'));
      // En cas d'erreur, initialiser calendars comme un tableau vide
      setCalendars([]);
      setSelectedCalendars([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLink = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setLinkError(null);
      setLinkSuccess(false);
      
      const result = await linkGoogleAccount();
      if (result) {
        setIsGoogleLinked(true);
        setLinkSuccess(true);
        await fetchCalendars();
      }
    } catch (error: any) {
      console.error("Erreur lors de la liaison avec Google:", error);
      setLinkError(error?.message || t('settings.errors.linkFailed', 'La connexion avec Google a échoué'));
    } finally {
      setLoading(false);
    }
  };

  const handleCalendarToggle = (calendarId: string) => {
    setSelectedCalendars(prev => 
      prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  const handleSaveCalendarPreferences = async () => {
    if (!currentUser) return;
    
    try {
      setSavingPrefs(true);
      await saveCalendarPreferences(selectedCalendars);
      setLinkSuccess(true);
      setTimeout(() => setLinkSuccess(false), 3000);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des préférences:", error);
      setLinkError(t('settings.errors.savePreferencesFailed', 'Impossible de sauvegarder vos préférences'));
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('settings.googleIntegration', 'Intégrations Google')}</CardTitle>
              <CardDescription>
                {t('settings.googleIntegrationDesc', 'Connectez vos comptes Google pour accéder à votre calendrier et vos emails.')}
              </CardDescription>
            </div>
            {isGoogleLinked && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                {t('settings.connected', 'Connecté')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {linkError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('settings.error', 'Erreur')}</AlertTitle>
              <AlertDescription>{linkError}</AlertDescription>
            </Alert>
          )}
          
          {linkSuccess && (
            <Alert variant="default" className="mb-4 bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>{t('settings.success', 'Succès')}</AlertTitle>
              <AlertDescription>
                {t('settings.operationSuccess', 'L\'opération a été effectuée avec succès')}
              </AlertDescription>
            </Alert>
          )}
          
          <Tabs defaultValue="calendar">
            <TabsList className="mb-4">
              <TabsTrigger value="calendar">
                <Calendar className="h-4 w-4 mr-2" />
                {t('settings.googleCalendar', 'Google Calendar')}
              </TabsTrigger>
              <TabsTrigger value="gmail">
                <Mail className="h-4 w-4 mr-2" />
                {t('settings.gmail', 'Gmail')}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="calendar">
              {isGoogleLinked ? (
                <>
                  <div className="mb-4 p-4 bg-slate-50 rounded-md">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">
                          {t('settings.googleAccountStatus', 'État du compte Google')}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {t('settings.accountConnected', 'Votre compte Google est connecté')}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleGoogleLink} disabled={loading}>
                        {loading ? t('common.loading', 'Chargement...') : t('settings.reconnect', 'Reconnecter')}
                      </Button>
                    </div>
                  </div>
                  
                  <Separator className="my-6" />
                  
                  <div className="mt-4">
                    <h3 className="text-lg font-medium mb-3">
                      {t('settings.selectCalendars', 'Sélectionnez les calendriers à partager')}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      {t('settings.selectCalendarsDesc', 'Choisissez les calendriers que vous souhaitez afficher dans votre tableau de bord.')}
                    </p>
                    
                    {loading ? (
                      <div className="py-4 text-center">
                        {t('common.loading', 'Chargement...')}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {!calendars || calendars.length === 0 ? (
                          <p>{t('settings.noCalendars', 'Aucun calendrier trouvé')}</p>
                        ) : (
                          Array.isArray(calendars) && calendars.map(calendar => (
                            <div key={calendar.id} className="flex items-center space-x-2 p-2 hover:bg-slate-50 rounded-md">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: calendar.backgroundColor || '#4285F4' }} 
                              />
                              <Checkbox
                                id={calendar.id}
                                checked={selectedCalendars.includes(calendar.id)}
                                onCheckedChange={() => handleCalendarToggle(calendar.id)}
                              />
                              <Label
                                htmlFor={calendar.id}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                              >
                                {calendar.summary}
                                {calendar.primary && (
                                  <Badge variant="secondary" className="ml-2 text-xs">
                                    {t('settings.primary', 'Principal')}
                                  </Badge>
                                )}
                              </Label>
                            </div>
                          ))
                        )}
                        
                        <Button
                          onClick={handleSaveCalendarPreferences}
                          disabled={savingPrefs || !calendars || calendars.length === 0}
                          className="mt-4"
                        >
                          {savingPrefs 
                            ? t('common.saving', 'Enregistrement...') 
                            : t('common.savePreferences', 'Enregistrer les préférences')}
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <p className="mb-4">
                    {t('settings.connectGoogleCalendarDesc', 'Connectez votre compte Google pour synchroniser vos événements de calendrier.')}
                  </p>
                  <Button 
                    onClick={handleGoogleLink} 
                    disabled={loading}
                    className="flex items-center"
                  >
                    {loading ? (
                      t('common.connecting', 'Connexion en cours...')
                    ) : (
                      <>
                        <Calendar className="h-4 w-4 mr-2" />
                        {t('settings.connectGoogleCalendar', 'Connecter Google Calendar')}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="gmail">
              {isGoogleLinked ? (
                <div>
                  <div className="mb-4 p-4 bg-slate-50 rounded-md">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">
                          {t('settings.gmailStatus', 'État de Gmail')}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {t('settings.gmailConnected', 'Votre compte Gmail est connecté')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <Separator className="my-6" />
                  
                  <div className="mt-6">
                    <h3 className="text-lg font-medium mb-3">
                      {t('settings.gmailConfig', 'Configuration Gmail')}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      {t('settings.gmailConfigDesc', 'Personnalisez comment les emails sont affichés et traités dans votre tableau de bord.')}
                    </p>
                    
                    {/* Options pour configurer Gmail - à développer ultérieurement */}
                    <div className="space-y-4 text-sm text-slate-500">
                      <p>{t('settings.gmailConfigComingSoon', 'Les options de configuration Gmail seront disponibles prochainement.')}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="mb-4">
                    {t('settings.connectGmailDesc', 'Connectez votre compte Google pour accéder à vos emails.')}
                  </p>
                  <Button 
                    onClick={handleGoogleLink} 
                    disabled={loading}
                    className="flex items-center"
                  >
                    {loading ? (
                      t('common.connecting', 'Connexion en cours...')
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        {t('settings.connectGmail', 'Connecter Gmail')}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
